// /setup: the homepage checklist as a full step-by-step guide (every step open, with the sourced FAQ sentences that
// back it), the Peppol ID search, and the "Is there an InvoiceNow login?" answer. No facts of its own: step text is
// the checklist's (checklist.step.*), and every cited sentence comes from content/facts.ts.
import { Claims } from '../Cite'
import { faq, inHouseConnect, inHouseMonths, myTaxMail, nowSteps, type Claim } from '../data'
import { PeppolChecker } from '../PeppolChecker'
import { useT } from '../i18n'
import { PageHead, Related } from '../shell'
import { MONO, bp, card, ext, para, sectionDark, wrapPad } from '../ui'

const fq = (id: string) => faq.find(f => f.id === id)!.claims
/** Sourced sentences shown under each checklist step, by step index. */
export const stepClaims: Claim[][] = [
  [myTaxMail],
  fq('software-ready'),
  [],
  [fq('what-is-it')[1]],
  [fq('acknowledgement')[0], inHouseConnect],
]
/** "Is there an InvoiceNow login?": you use InvoiceNow through your solution, which also shows your submissions. */
export const loginClaims: Claim[] = [fq('what-is-it')[1], fq('acknowledgement')[1]]
/** FAQ entries whose sentences this page quotes, so their translations gate this page too (src/i18n/content.ts). */
export const SETUP_FAQ = ['software-ready', 'what-is-it', 'acknowledgement']

export default function SetupPage() {
  const { t, L } = useT()
  return (
    <main>
      <PageHead path="/setup" slug="setup" />
      <section id="steps" style={sectionDark}>
        <div style={{ ...wrapPad, display: 'flex', flexDirection: 'column', gap: 'var(--gap)' }}>
          <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
            {nowSteps.map((s, i) => {
              let n = 0
              return (
                <li key={i} id={`step-${i + 1}`} style={{ ...card, scrollMarginTop: 96 }}>
                  <span style={{ fontFamily: MONO, fontSize: 12, letterSpacing: '0.1em', color: '#b2f5dc' }}>{t('setup.step', { n: i + 1, total: nowSteps.length })}</span>
                  <h2 style={{ margin: 0, fontSize: 'clamp(24px,3vw,34px)', fontWeight: 500, letterSpacing: '-0.02em', lineHeight: 1.15 }}>{t(`checklist.step.${i}.title`)}</h2>
                  <p style={para}>{t(`checklist.step.${i}.body`, inHouseMonths)}</p>
                  {stepClaims[i].length > 0 && <p style={{ ...para, fontSize: 15.5, color: '#c3d8d0' }}><Claims claims={stepClaims[i]} /></p>}
                  {s.tool === 'peppol' && <PeppolChecker />}
                  <div className="btn-stack" style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    {s.actions.flat().map(([h, filled]) => {
                      const l = t(`checklist.step.${i}.a.${n++}`)
                      return <a key={l} href={L(h)} {...(/^[#/]/.test(h) ? {} : ext)} {...bp(!!filled)}>{l} →</a>
                    })}
                  </div>
                </li>
              )
            })}
          </ol>
          <div id="login" style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 860, scrollMarginTop: 96 }}>
            <h2 style={{ margin: 0, fontSize: 'clamp(24px,3vw,34px)', fontWeight: 500, letterSpacing: '-0.02em', lineHeight: 1.15 }}>{t('setup.loginTitle')}</h2>
            <p style={para}>{t('setup.loginLead')} <Claims claims={loginClaims} /></p>
          </div>
          <p style={{ margin: 0, fontSize: 13, color: '#9dbdb2' }}>{t('setup.trackerNote')} <a href={L('/#what-to-do')}>{t('setup.trackerLink')} →</a></p>
        </div>
      </section>
      <Related paths={['/when', '/free-software', '/grants']} />
    </main>
  )
}
